import React from 'react';
import { FONTS } from '../../styles/tokens';

/**
 * Structured view of an app's current lifecycle state. We derive it from
 * the deployed_apps row's build_status + deploy_status so the admin
 * panel can show "Deploying", "Building", "Theming" etc. rather than
 * just a binary active/disabled.
 */
export type AppStatusVariant =
  | 'active'
  | 'building'
  | 'deploying'
  | 'theming'
  | 'build_failed'
  | 'deploy_failed'
  | 'stopped'
  | 'disabled'
  | 'unknown';

export interface DeployedAppSummary {
  build_status?: string | null;
  deploy_status?: string | null;
  /** Most recent pipeline stage seen on the SSE stream — optional. */
  current_stage?: string | null;
}

interface StatusMeta {
  variant: AppStatusVariant;
  label: string;
  color: string;
  /** Softer tint used for the pill background. */
  bg: string;
  /** When true the status dot pulses — indicates work-in-progress. */
  pulsing: boolean;
}

const META: Readonly<Record<AppStatusVariant, StatusMeta>> = {
  active:         { variant: 'active',        label: 'Active',         color: '#4ADE80', bg: 'rgba(74,222,128,0.15)',  pulsing: false },
  building:       { variant: 'building',      label: 'Building image', color: '#00E0FF', bg: 'rgba(0,224,255,0.14)',   pulsing: true  },
  deploying:      { variant: 'deploying',     label: 'Deploying',      color: '#01A9ED', bg: 'rgba(1,169,237,0.16)',   pulsing: true  },
  theming:        { variant: 'theming',       label: 'Creating theme', color: '#A78BFA', bg: 'rgba(167,139,250,0.14)', pulsing: true  },
  build_failed:   { variant: 'build_failed',  label: 'Build failed',   color: '#EF4444', bg: 'rgba(239,68,68,0.15)',   pulsing: false },
  deploy_failed:  { variant: 'deploy_failed', label: 'Deploy failed',  color: '#EF4444', bg: 'rgba(239,68,68,0.15)',   pulsing: false },
  stopped:        { variant: 'stopped',       label: 'Stopped',        color: '#F59E0B', bg: 'rgba(245,158,11,0.15)',  pulsing: false },
  disabled:       { variant: 'disabled',      label: 'Disabled',       color: '#94A3B8', bg: 'rgba(148,163,184,0.15)', pulsing: false },
  unknown:        { variant: 'unknown',       label: 'Unknown',        color: '#94A3B8', bg: 'rgba(148,163,184,0.12)', pulsing: false },
};

/**
 * Decide which status to show given a Keycloak client and the matching
 * `deployed_apps` row. Priority: explicit failure → in-progress stage →
 * running/active → stopped → disabled → unknown.
 */
export function resolveAppStatus(input: {
  clientEnabled: boolean;
  deployed?: DeployedAppSummary | null;
}): AppStatusVariant {
  const deployed = input.deployed;

  if (deployed?.build_status === 'build_failed') return 'build_failed';
  if (deployed?.deploy_status === 'deploy_failed') return 'deploy_failed';

  const stage = (deployed?.current_stage ?? '').toLowerCase();
  if (stage === 'keycloak') return 'theming';
  if (stage === 'build' || stage === 'dockerfile' || stage === 'analyze') return 'building';

  if (deployed?.build_status === 'building') return 'building';
  if (deployed?.deploy_status === 'pending' && deployed?.build_status === 'built') return 'deploying';

  if (deployed?.deploy_status === 'running' && input.clientEnabled) return 'active';
  if (deployed?.deploy_status === 'stopped') return 'stopped';

  if (input.clientEnabled && !deployed) return 'active';
  if (!input.clientEnabled) return 'disabled';
  return 'unknown';
}

/** Visual pill showing the resolved status with colored dot + label. */
export function AppStatusBadge(props: { status: AppStatusVariant }): React.JSX.Element {
  const meta = META[props.status] ?? META.unknown;
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '6px',
        padding: '3px 10px', borderRadius: '999px',
        backgroundColor: meta.bg,
        fontFamily: FONTS.body, fontSize: '11px', fontWeight: 600,
        color: meta.color,
        border: `1px solid ${meta.color}22`,
        lineHeight: 1.2,
      }}
    >
      <span
        style={{
          width: '7px', height: '7px', borderRadius: '50%',
          backgroundColor: meta.color,
          animation: meta.pulsing ? 'appStatusPulse 1.6s ease-in-out infinite' : 'none',
        }}
      />
      {meta.label}
      <style>{meta.pulsing ? keyframes : ''}</style>
    </span>
  );
}

/** Keyframes injected alongside pulsing badges — scoped via class-less @keyframes. */
const keyframes = `
@keyframes appStatusPulse {
  0%, 100% { opacity: 1; box-shadow: 0 0 0 0 currentColor; }
  50% { opacity: 0.55; box-shadow: 0 0 0 4px rgba(255,255,255,0); }
}
`;

/** Fetch deployed-app lifecycle summaries keyed by slug for quick lookup. */
export async function fetchDeployedAppsBySlug(token: string): Promise<Record<string, DeployedAppSummary>> {
  try {
    const response = await fetch('/api/apps/deployed', {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) return {};
    const rows = (await response.json()) as Array<{ slug: string; build_status?: string; deploy_status?: string }>;
    const bySlug: Record<string, DeployedAppSummary> = {};
    for (const row of rows) {
      bySlug[row.slug] = { build_status: row.build_status, deploy_status: row.deploy_status };
    }
    return bySlug;
  } catch {
    return {};
  }
}
